import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { SmallText } from "../../../components/core/Utility";
import "primeicons/primeicons.css";

const FulfilmentTd = styled.td`
  background-color: #d6f2e0;
  padding: 2px 8px;
  vertical-align: top;
  border: none;
  box-shadow: none;
  line-height: 1.1;
`;

const ReturnedTd = styled.td`
  background-color: #fbe4d7;
  padding: 2px 8px;
  vertical-align: top;
  border: none;
  box-shadow: none;
  line-height: 1.1;
`;

const SpacerRow = styled.tr`
  height: 5px;
  background: #fff;
  padding: 0;
  border: none;
`;

const TableContainer = styled.div`
  font-family: Verizonn-NHG-eTx, Helvetica, Arial, sans-serif;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;

  /* Custom scrollbar styles */
  ::-webkit-scrollbar {
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const StyledTable = styled.table`
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 0.85em;
  margin-bottom: 10px;
  border-top: 1px solid #000000;
  box-shadow: none;
  border-radius: 0;
  white-space: nowrap;

  th {
    background-color: #ffffff;
    text-align: left;
    font-weight: bold;
    border-bottom: none !important;
    border-radius: 0;
    box-shadow: none;
    padding: 8px 12px;
    word-wrap: break-word;
    word-break: break-word;
    white-space: normal;
  }

  /* Subscription Name column (2nd column) */
  th:nth-child(2),
  td:nth-child(2) {
    width: 160px !important;
    max-width: 180px !important;
    min-width: 180px !important;
    word-wrap: break-word !important;
    word-break: break-word !important;
    white-space: normal !important;
    overflow-wrap: break-word !important;
    hyphens: auto !important;
  }

  /* Tech Ref ID column (3rd column) */
  th:nth-child(3),
  td:nth-child(3) {
    width: 120px !important;
    max-width: 120px !important;
    min-width: 120px !important;
    word-wrap: break-word !important;
    word-break: break-word !important;
    white-space: normal !important;
    overflow-wrap: break-word !important;
    hyphens: auto !important;
  }

  td {
    vertical-align: top;
    border: none;
    box-shadow: none;
    line-height: 1.1;
    padding: 4px 12px;
    word-wrap: break-word;
    word-break: break-word;
    white-space: normal;
  }

  tr {
    margin: 0;
    height: auto;
    min-height: 0;
    border: none;
  }
`;

export const OrderShippingSummaryGrid = ({ noDataMessage = "No data Available", rows = [] }) => {
  const [sortedRows, setSortedRows] = useState(rows);

  useEffect(() => {
    setSortedRows(rows);
  }, [rows]);

  if (!sortedRows.length) {
    return <SmallText>{noDataMessage}</SmallText>;
  }

  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <tr>
            <th>
              <SmallText bold>Bundle Id</SmallText>
            </th>
            <th>
              <SmallText bold>Subscription Name</SmallText>
            </th>
            <th>
              <SmallText bold>Tech Ref ID</SmallText>
            </th>
            <th>
              <SmallText bold>Category</SmallText>
            </th>
            <th>
              <SmallText bold>Sub Category</SmallText>
            </th>
            <th>
              <SmallText bold>Ordered</SmallText>
            </th>
            <th>
              <SmallText bold>Fallout</SmallText>
            </th>
            <th>
              <SmallText bold>Remaining</SmallText>
            </th>
            <th>
              <SmallText bold>Fulfilled</SmallText>
            </th>
            <th>
              <SmallText bold>Submitted</SmallText>
            </th>
            <th>
              <SmallText bold>Created</SmallText>
            </th>
            <th>
              <SmallText bold>Back-Ordered</SmallText>
            </th>
            <th>
              <SmallText bold>Packaging</SmallText>
            </th>
            <th>
              <SmallText bold>Shipped</SmallText>
            </th>
            <th>
              <SmallText bold>Delivered</SmallText>
            </th>
            <th>
              <SmallText bold>Returned</SmallText>
            </th>
            <th>
              <SmallText bold>Submitted</SmallText>
            </th>
            <th>
              <SmallText bold>Created</SmallText>
            </th>
            <th>
              <SmallText bold>Back-Ordered</SmallText>
            </th>
            <th>
              <SmallText bold>Packaging</SmallText>
            </th>
            <th>
              <SmallText bold>Shipped</SmallText>
            </th>
            <th>
              <SmallText bold>Delivered</SmallText>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((vehicle, vehicleIndex) => {
            const spacer =
              vehicleIndex > 0 ? (
                <SpacerRow key={`spacer-${vehicle?.bundleId}`}>
                  <td colSpan={14} />
                </SpacerRow>
              ) : null;

            return (
              <React.Fragment key={vehicle?.bundleId}>
                {spacer}
                {vehicle?.summaryLines.map((material, materialIndex) => {
                  const isFirstMaterial = materialIndex === 0;
                  return (
                    <tr key={`${vehicle?.bundleId}-${material?.subscriptionId}-${materialIndex}`}>
                      {isFirstMaterial ? (
                        <td rowSpan={vehicle?.summaryLines?.length}>
                          <SmallText>{vehicle?.bundleId}</SmallText>
                        </td>
                      ) : null}
                      <td>
                        <SmallText>
                          {material?.subscription
                            ? `${material?.subscription}${material?.subscriptionId ? "(" + material?.subscriptionId + ")" : ""}`
                            : "--"}
                        </SmallText>
                      </td>
                      <td>
                        <SmallText>{material?.techRefId || "--"}</SmallText>
                      </td>
                      <td>
                        <SmallText>{material?.category || "--"}</SmallText>
                      </td>
                      <td>
                        <SmallText>{material?.subCategory || "--"}</SmallText>
                      </td>
                      <td>
                        <SmallText>{material?.ordered || "--"}</SmallText>
                      </td>
                      <td>
                        <SmallText>{material?.fallout || "--"}</SmallText>
                      </td>
                      <td>
                        <SmallText>{material?.remaining || "--"}</SmallText>
                      </td>
                      <td>
                        <SmallText>{material?.fulfilled || "--"}</SmallText>
                      </td>
                      <FulfilmentTd>
                        <SmallText>{material?.submitted || "--"}</SmallText>
                      </FulfilmentTd>
                      <FulfilmentTd>
                        <SmallText>{material?.created || "--"}</SmallText>
                      </FulfilmentTd>
                      <FulfilmentTd>
                        <SmallText>{material?.backordered || "--"}</SmallText>
                      </FulfilmentTd>
                      <FulfilmentTd>
                        <SmallText>{material?.packaging || "--"}</SmallText>
                      </FulfilmentTd>
                      <FulfilmentTd>
                        <SmallText>{material?.shipped || "--"}</SmallText>
                      </FulfilmentTd>
                      <FulfilmentTd>
                        <SmallText>{material?.delivered || "--"}</SmallText>
                      </FulfilmentTd>
                      <ReturnedTd>
                        <SmallText>{material?.returned || "--"}</SmallText>
                      </ReturnedTd>
                      <FulfilmentTd>
                        <SmallText>{material?.submittedR || "--"}</SmallText>
                      </FulfilmentTd>
                      <FulfilmentTd>
                        <SmallText>{material?.createdR || "--"}</SmallText>
                      </FulfilmentTd>
                      <FulfilmentTd>
                        <SmallText>{material?.backorderedR || "--"}</SmallText>
                      </FulfilmentTd>
                      <FulfilmentTd>
                        <SmallText>{material?.packagingR || "--"}</SmallText>
                      </FulfilmentTd>
                      <FulfilmentTd>
                        <SmallText>{material?.shippedR || "--"}</SmallText>
                      </FulfilmentTd>
                      <FulfilmentTd>
                        <SmallText>{material?.deliveredR || "--"}</SmallText>
                      </FulfilmentTd>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </StyledTable>
    </TableContainer>
  );
};
